import { v4 as uuidv4 } from 'uuid';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isEmpty } from 'radash';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconArrowsMove, IconTrash } from '@tabler/icons-react';
import { useMenuVisibility, MENU_BREAKPOINTS_WITH_DRAG } from '../../hooks/useMenuVisibility';
import TimelineScenes from '../TimelineScenes/TimelineScenes';
import Deletes from '../Deletes';
import TotalCompositeDuration from '../TotalCompositeDuration';
import TimelineSpeakers from '../TimelineSpeakers';
import TimelineSelect from '../TimelineSelect';
import TrimHandles from './TrimHandles';
import TimelineEffectsLane from '../../TimelineEffects/TimelineEffectsLane';
import { getDeletesForSegment, getEndForSegment, getStartForSegment } from '../../playerConfigUtil';
import { updateClipData } from '../../clipUpdateDataUtils';
import { CLIP_UPDATE_TYPE } from '../../clipUpdateDataUtils/constants';
import { TIMELINE_COMPONENT_CLASS_IDENTIFIER } from '../../constants';
import { getAllCompositeSegments } from '../../clipUpdateDataUtilsWithSplits/compositeUtils';
import { COMPOSITION_DIMENSIONS, VIDEO_QUALITY } from '@/App/remotion/constants';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { FitType } from '@/domains/asset';
import { Scene, Scenes } from '@/App/remotion/MainPlayer/types/Scene';
import { getVideosAndTimeRanges } from '@/App/remotion/MainPlayer/utils/playerConfig/compositeSegment/utils';
import { LayoutType } from '@/App/remotion/MainPlayer/utils/speakerLayoutGenerator/types';
import { Sequence } from '@/App/remotion/MainPlayer/types/Sequence';
import { classnames } from '@/libs/utils';
import { CompositeSegment } from '@/App/remotion/MainPlayer/types/Segment/CompositeSegment';
import { getSegmentsTrack } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/utils';
import { isSplitsInTimelineEnabled } from '@/stores/clip';

function TimelineCompositeSegment({
  setCompositeSegmentDragStart,
  setCompositeSegmentDragEnd,
  compositeSegmentDragStart,
  compositeSegmentDragEnd,
  newTimelineContainerRef,
  speakerTimelineContainerRef,
  setCompositeSegmentDragId,
  compositeSegment,
  compositeTimelineRef,
  compositeSegmentDragId
}: {
  setCompositeSegmentDragStart: (value: number) => void;
  setCompositeSegmentDragEnd: (value: number) => void;
  compositeSegmentDragStart: number;
  compositeSegmentDragEnd: number;
  newTimelineContainerRef: React.RefObject<HTMLDivElement>;
  speakerTimelineContainerRef: React.RefObject<HTMLDivElement>;
  setCompositeSegmentDragId: (value: string | null) => void;
  compositeSegment: CompositeSegment;
  compositeTimelineRef: React.RefObject<HTMLDivElement>;
  compositeSegmentDragId: string | null;
}) {
  const [showTrimHandlesOnHover, setShowTrimHandlesOnHover] = useState<'start' | 'end' | null>(null);
  const { clipData, clipId, fps } = useClipsContext();
  const isTrimmingSomeOtherSegment = useMemo(
    () => compositeSegmentDragId && compositeSegmentDragId !== compositeSegment.id,
    [compositeSegmentDragId, compositeSegment.id]
  );
  const isTrimmingCurrentSegment = useMemo(
    () => compositeSegmentDragId === compositeSegment.id,
    [compositeSegmentDragId, compositeSegment.id]
  );

  const playerConfig = clipData.asset_metadata.player_config;
  const totalSegmentCount = useMemo(
    () => (playerConfig && getSegmentsTrack(playerConfig).segments.length) || 0,
    [playerConfig]
  );
  const currentSegmentIndex = useMemo(
    () =>
      playerConfig && getSegmentsTrack(playerConfig).segments.findIndex(segment => segment.id === compositeSegment.id),
    [playerConfig, compositeSegment]
  );
  const isFirstSegment = useMemo(() => currentSegmentIndex === 0, [currentSegmentIndex]);
  const isLastSegment = useMemo(
    () => currentSegmentIndex === totalSegmentCount - 1,
    [currentSegmentIndex, totalSegmentCount]
  );

  const compositeSegmentRef = useRef<HTMLDivElement | null>(null);
  const [showDragHandle, setShowDragHandle] = useState(false);
  const menuVisibility = useMenuVisibility(compositeSegmentRef, MENU_BREAKPOINTS_WITH_DRAG);

  const showTime = menuVisibility === 'all';
  const showTrash = menuVisibility === 'all' || menuVisibility === 'no-time';
  const showDrag = menuVisibility === 'all' || menuVisibility === 'no-time' || menuVisibility === 'drag-only';

  const removeCompositeSegment = useCallback(() => {
    updateClipData({
      clipId,
      actionType: CLIP_UPDATE_TYPE.REMOVE_SEGMENT_TRACK,
      id: compositeSegment.id
    });
  }, [clipId, compositeSegment.id]);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: compositeSegment.id,
    disabled: false
  });

  const style = {
    transform: CSS.Transform.toString(transform ? { ...transform, scaleX: 1, scaleY: 1 } : null),
    transition
  };

  const isSplitsEnabled = useMemo(() => {
    return isSplitsInTimelineEnabled(clipData);
  }, [clipData]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!compositeSegmentRef.current) {
        return;
      }

      const rect = compositeSegmentRef.current.getBoundingClientRect();
      const mouseX = e.clientX;
      const segmentLeft = rect.left;
      const segmentRight = rect.right;
      const hoverThreshold = 20; // pixels from edge to trigger hover
      const trimBarsWidth = 18;

      // Check if mouse is near the left or right edge
      const isNearLeftEdge =
        mouseX >= segmentLeft - (showTrimHandlesOnHover ? trimBarsWidth : 0) && mouseX <= segmentLeft + hoverThreshold;
      const isNearRightEdge =
        mouseX <= segmentRight + (showTrimHandlesOnHover ? trimBarsWidth : 0) &&
        mouseX >= segmentRight - hoverThreshold;
      setShowTrimHandlesOnHover(isNearLeftEdge ? 'start' : isNearRightEdge ? 'end' : null);
    };

    const handleMouseLeave = () => {
      setShowTrimHandlesOnHover(null);
    };

    const element = compositeSegmentRef.current;
    if (element) {
      element.addEventListener('mousemove', handleMouseMove);
      element.addEventListener('mouseleave', handleMouseLeave);
    }

    return () => {
      if (element) {
        element.removeEventListener('mousemove', handleMouseMove);
        element.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, [showTrimHandlesOnHover]);

  const timelineSelectionEnabled = !isSplitsEnabled;
  // Calculate timeline width and duration
  const timelineDuration =
    getEndForSegment(clipData, compositeSegment.id) - getStartForSegment(clipData, compositeSegment.id);

  const effects = useMemo(() => compositeSegment?.effects || [], [compositeSegment?.effects]);
  // TODO: @AshwinBhatkal - to be removed upon implementing clip creation on BE - 91dag8e1yg8a678e29
  const scenes: Scenes = useMemo(() => {
    if (!compositeSegment) {
      return [];
    }

    const scenes = compositeSegment.scenes;

    if (scenes.length === 0) {
      const all_video_ids = getVideosAndTimeRanges({ clip: clipData }).map(video => video.videoId);
      const defaultScene: Scene = {
        id: uuidv4(),
        name: 'Scene 1',
        fromFrame: compositeSegment.currentBounds.fromFrame,
        toFrame: compositeSegment.currentBounds.toFrame,
        details: {
          layout: clipData.asset_metadata.layout as LayoutType,
          fitMode: clipData.asset_metadata.fit as FitType,
          visible_video_ids: all_video_ids,
          all_video_ids: all_video_ids
        }
      };

      return [defaultScene];
    }

    return scenes;
    // Other dependencies not require - if we set scene once, we don't need the other dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compositeSegment?.scenes]);

  const compositeSegmentDeleteBounds: Sequence[] = useMemo(() => {
    const segmentDeletes = getDeletesForSegment(clipData, compositeSegment.id);
    if (segmentDeletes && !isEmpty(segmentDeletes)) {
      return Object.values(segmentDeletes)
        .map(d => ({
          fromFrame: d.bounds[0] * fps,
          toFrame: d.bounds[1] * fps
        }))
        .sort((a, b) => a.fromFrame - b.fromFrame)
        .filter(d => d.fromFrame !== d.toFrame);
    }

    return [];
  }, [clipData, compositeSegment.id, fps]);

  const { compositionHeight, compositionWidth } = useMemo(() => {
    const videoSize = clipData.asset_metadata.size;

    const videoQuality = VIDEO_QUALITY.FULL_HD;
    const compositionHeight = COMPOSITION_DIMENSIONS[videoSize][videoQuality].HEIGHT;
    const compositionWidth = COMPOSITION_DIMENSIONS[videoSize][videoQuality].WIDTH;

    return {
      compositionHeight,
      compositionWidth
    };
  }, [clipData.asset_metadata.size]);

  const recordingHasEffects = useMemo(() => {
    return getAllCompositeSegments(clipData).some(segment => segment.effects?.length && segment.effects?.length > 0);
  }, [clipData]);

  const setNodeRefForCompositeSegment = useCallback(
    (node: HTMLDivElement | null) => {
      setNodeRef(node);
      compositeSegmentRef.current = node;
    },
    [setNodeRef]
  );
  const canDeleteCompositeSegment = useMemo(() => {
    return getAllCompositeSegments(clipData).length > 1 && isSplitsEnabled;
  }, [clipData, isSplitsEnabled]);

  // Used to force composite time and hover effect when cursor is outside of the segment during drag
  const forceShowHoverItems =
    !isTrimmingSomeOtherSegment && (showDragHandle || showTrimHandlesOnHover || isTrimmingCurrentSegment);

  return (
    <div
      ref={setNodeRefForCompositeSegment}
      style={{
        width: `calc(${
          compositeSegment
            ? (compositeSegment.currentBounds.toFrame / fps +
                compositeSegmentDragEnd -
                (compositeSegment.currentBounds.fromFrame / fps + compositeSegmentDragStart)) *
              fps
            : 0
        }px * var(--timeline-zoom))`,
        paddingLeft: isFirstSegment ? '0px' : '1.5px',
        paddingRight: isLastSegment ? '0px' : '1.5px',
        ...style
      }}
      className={classnames('group/composite-segment relative', isDragging ? 'z-50 opacity-50' : '')}
      data-testid="timeline-composite-segment"
      onMouseDown={e => {
        if (isDragging) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      onMouseEnter={() => setShowDragHandle(true)}
      onMouseLeave={() => setShowDragHandle(false)}
    >
      <div className="relative size-full">
        {forceShowHoverItems && menuVisibility !== 'none' && (
          <div
            className={classnames(
              'absolute bottom-[8px] right-[10px] z-50 flex items-center gap-0 rounded-md bg-black px-1.5 py-1 text-white opacity-0 shadow-lg transition-opacity duration-200 group-hover/composite-segment:opacity-100 group-hover:opacity-100',
              TIMELINE_COMPONENT_CLASS_IDENTIFIER.ELEMENT_MENU_CONTAINER
            )}
            style={{
              opacity: forceShowHoverItems ? 1 : undefined
            }}
          >
            {showTime && (
              <div className="mr-1 text-xs text-white">
                <TotalCompositeDuration compositeSegment={compositeSegment} />
              </div>
            )}
            {isSplitsEnabled && showDrag && (
              <div
                {...attributes}
                {...listeners}
                className="flex cursor-move items-center justify-center rounded p-1.5 text-white transition-all duration-200 hover:bg-white/10 active:cursor-grabbing"
                data-testid="composite-segment-drag-handle"
              >
                <IconArrowsMove size={12} className="text-white" />
              </div>
            )}
            {isSplitsEnabled && showTrash && (
              <div
                onClick={e => {
                  if (canDeleteCompositeSegment) {
                    e.stopPropagation();
                    removeCompositeSegment();
                  }
                }}
                className={classnames(
                  'group/delete flex cursor-pointer items-center justify-center rounded p-1.5 text-white transition-all duration-200 hover:bg-red-500/20 active:bg-red-500/30',
                  canDeleteCompositeSegment ? '' : 'cursor-not-allowed opacity-50'
                )}
                data-testid="composite-segment-delete-button"
              >
                <IconTrash
                  size={12}
                  className="text-white transition-colors duration-200 group-hover/delete:text-red-500"
                />
              </div>
            )}
          </div>
        )}
        <div
          className={classnames(
            'absolute inset-0 rounded-[7px] border-2 border-slate-200',
            forceShowHoverItems ? 'border-slate-950' : ''
          )}
        ></div>
        <div className="relative flex flex-col gap-1 overflow-hidden py-[4px]" ref={compositeTimelineRef}>
          {effects?.length > 0 ? (
            <TimelineEffectsLane
              effects={effects}
              duration={timelineDuration}
              newTimelineContainerRef={newTimelineContainerRef}
              speakerTimelineContainerRef={speakerTimelineContainerRef}
              startOffsetSeconds={getStartForSegment(clipData, compositeSegment.id)}
            />
          ) : recordingHasEffects ? (
            <div className="flex w-full flex-col overflow-hidden" style={{ pointerEvents: 'auto' }}>
              <div className="relative flex h-[32px] min-h-6 w-full items-center"></div>
            </div>
          ) : null}
          {compositeSegment ? (
            <TimelineScenes
              scenes={scenes}
              compositeSegment={compositeSegment}
              compositeSegmentDragStart={compositeSegmentDragStart}
              compositeSegmentDragEnd={compositeSegmentDragEnd}
              videoSize={clipData.asset_metadata.size}
              showBorder={!clipData.asset_metadata.hide_borders}
              playerConfig={clipData.asset_metadata.player_config}
              compositeSegmentDeleteBounds={compositeSegmentDeleteBounds}
              newTimelineContainerRef={newTimelineContainerRef}
              speakerTimelineContainerRef={speakerTimelineContainerRef}
              compositionHeight={compositionHeight}
              compositionWidth={compositionWidth}
            />
          ) : (
            <div className="mx-1 h-[30px] rounded-[3px] bg-slate-200"></div>
          )}
          {compositeSegment && (
            <TimelineSpeakers
              compositeSegmentDragStart={compositeSegmentDragStart}
              compositeSegmentDragEnd={compositeSegmentDragEnd}
              compositeSegment={compositeSegment}
            />
          )}
          <Deletes
            compositeSegment={compositeSegment}
            compositeSegmentDragStart={compositeSegmentDragStart}
            compositeSegmentDragEnd={compositeSegmentDragEnd}
            newTimelineContainerRef={newTimelineContainerRef}
            speakerTimelineContainerRef={speakerTimelineContainerRef}
          />
          {timelineSelectionEnabled && (
            <TimelineSelect
              compositeSegment={compositeSegment}
              compositeSegmentRef={compositeSegmentRef}
              speakerTimelineContainerRef={speakerTimelineContainerRef}
            />
          )}
        </div>
      </div>
      {compositeSegment && (
        <TrimHandles
          fps={fps}
          clipId={clipId}
          clipData={clipData}
          compositeSegment={compositeSegment}
          showTrimsBars={isTrimmingSomeOtherSegment ? null : showTrimHandlesOnHover}
          newTimelineContainerRef={newTimelineContainerRef}
          speakerTimelineContainerRef={speakerTimelineContainerRef}
          compositeSegmentDragStart={compositeSegmentDragStart}
          compositeSegmentDragEnd={compositeSegmentDragEnd}
          setCompositeSegmentDragStart={setCompositeSegmentDragStart}
          setCompositeSegmentDragEnd={setCompositeSegmentDragEnd}
          setCompositeSegmentDragId={setCompositeSegmentDragId}
        />
      )}
    </div>
  );
}

export default memo(TimelineCompositeSegment);
